// ImportList.tsx (diff-style rewrite, повний компонент)
import React, { useRef, useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Upload, RefreshCw, Play, Undo2 } from "lucide-react"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchDropdown from "~/library/CatchDropdown"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { useToast } from "~/utils/Toast"
import { apiWorkspaceImports } from "~/routes"

import {
    normalizeImports,
    niceDate,
    statusBadgeClass,
    displayName,
    ImportRow,
    uploadImport,
    postImportAction,
} from "~/utils/imports"

const ImportList: React.FC = () => {
    const { venueSlug } = getAppConfig()
    const navigate = useNavigate()
    const toast = useToast()

    const fileRef = useRef<HTMLInputElement | null>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [runningId, setRunningId] = useState<string | null>(null)
    const [rollingBackId, setRollingBackId] = useState<string | null>(null)

    const endpoint = apiWorkspaceImports.index.path({ venue_slug: venueSlug })
    const { data, isLoading, refetch } = useFetchResource<any, "imports">(endpoint, "imports", {})

    const imports = useMemo(() => normalizeImports(data), [data])

    const handlePickFile = () => fileRef.current?.click()

    const handleUpload = async (file: File) => {
        setIsUploading(true)
        try {
            const url = apiWorkspaceImports.create.path({ venue_slug: venueSlug })
            const res = await uploadImport({ url, file })

            if (res.ok) {
                toast("Import created", "success")
                navigate(`/imports/${res.id}`)
            } else {
                toast(`Upload failed. ${res.error}`, "danger")
            }
        } catch (e) {
            console.error(e)
            toast("Upload failed. Please try again", "danger")
        } finally {
            setIsUploading(false)
            if (fileRef.current) fileRef.current.value = ""
        }
    }

    const handleRun = async (id: string) => {
        setRunningId(id)
        try {
            const url = apiWorkspaceImports.run.path({ venue_slug: venueSlug, id })
            const res = await postImportAction({ url })

            if (res.ok) {
                toast("Import started", "success")
                await refetch?.()
                navigate(`/imports/${id}`)
            } else {
                toast(res.error || "Run failed. Please try again", "danger")
            }
        } catch (e) {
            console.error(e)
            toast("Run failed. Please try again", "danger")
        } finally {
            setRunningId(null)
        }
    }

    const handleRollback = async (id: string) => {
        setRollingBackId(id)
        try {
            const url = apiWorkspaceImports.rollback.path({ venue_slug: venueSlug, id })
            const res = await postImportAction({ url })

            if (res.ok) {
                toast("Rollback completed", "success")
                await refetch?.()
                navigate(`/imports/${id}`)
            } else {
                toast(res.error || "Rollback failed. Please try again", "danger")
            }
        } catch (e) {
            console.error(e)
            toast("Rollback failed. Please try again", "danger")
        } finally {
            setRollingBackId(null)
        }
    }

    const pageLoading = isLoading || isUploading

    return (
        <LoadingWrapper isLoading={pageLoading}>
            <PageTitle title="Imports">
                <CatchDropdown
                    label="Actions"
                    className="ms-2"
                    items={[
                        { label: "Upload Excel", onClick: handlePickFile, icon: Upload },
                        { label: "Refresh", onClick: () => refetch?.(), icon: RefreshCw },
                    ]}
                />
            </PageTitle>

            <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                className="d-none"
                onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) handleUpload(f)
                }}
            />

            <CatchCard title="Recent Imports">
                <div className="table-responsive">
                    <table className="table table-sm align-middle">
                        <thead>
                        <tr>
                            <th>File</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Updated</th>
                            <th style={{ width: 160 }} />
                        </tr>
                        </thead>
                        <tbody>
                        {imports.map((imp: ImportRow) => {
                            const canRun = imp.status === "pending" || imp.status === "failed"
                            const canRollback =
                                imp.status === "completed" || imp.status === "completed_with_errors" || imp.status === "failed"

                            const isRunning = runningId === imp.id
                            const isRollingBack = rollingBackId === imp.id
                            const busy = isRunning || isRollingBack

                            return (
                                <tr key={imp.id}>
                                    <td style={{ cursor: "pointer" }} onClick={() => navigate(`/imports/${imp.id}`)}>
                                        <span title={imp.id}>{displayName(imp)}</span>
                                    </td>
                                    <td>
                                        <span className={statusBadgeClass(imp.status)}>{imp.status}</span>
                                    </td>
                                    <td>{niceDate(imp.created_at)}</td>
                                    <td>{niceDate(imp.updated_at)}</td>
                                    <td className="text-end">
                                        <div className="btn-group btn-group-sm" role="group">
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary"
                                                disabled={!canRun || busy}
                                                onClick={() => handleRun(imp.id)}
                                            >
                                                <Play size={16} className="me-1" />
                                                {isRunning ? "Running..." : "Run"}
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-outline-danger"
                                                disabled={!canRollback || busy}
                                                onClick={() => handleRollback(imp.id)}
                                            >
                                                <Undo2 size={16} className="me-1" />
                                                {isRollingBack ? "Rolling..." : "Rollback"}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}

                        {imports.length === 0 && (
                            <tr>
                                <td colSpan={5} className="text-muted">
                                    No imports yet. Upload an Excel file to start.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </CatchCard>
        </LoadingWrapper>
    )
}

export default ImportList
